import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { ChevronLeft, ChevronRight, Bell, CheckCircle2, Clock, AlertTriangle, Search, Filter } from "lucide-react";

function initials(name = "") {
  return name.split(" ").filter(Boolean).slice(0, 2).map(p => p[0]).join("").toUpperCase();
}

function avatarColor(name = "") {
  const palette = ["#1a3a5c", "#c8232c", "#0f766e", "#7c3aed", "#b45309", "#0369a1", "#be123c", "#4338ca"];
  let h = 0; for (const c of name) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return palette[h % palette.length];
}

const STATUS_ICON = {
  completed: <CheckCircle2 className="w-3 h-3 text-emerald-600" />,
  in_progress: <Clock className="w-3 h-3 text-amber-600" />,
  pending: <AlertTriangle className="w-3 h-3 text-slate-400" />,
};
const STATUS_LABEL = { completed: "Concluído", in_progress: "Em andamento", pending: "Pendente" };

export default function CollaboratorsCarousel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [pos, setPos] = useState("all");
  const [dep, setDep] = useState("all");
  const [br, setBr] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all"); // all | recycling | ok
  const [depMap, setDepMap] = useState({});
  const [brMap, setBrMap] = useState({});
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get("/dashboard/collaborators?limit=500")
      .then((r) => setItems(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
    Promise.all([api.get("/departments"), api.get("/branches")]).then(([d, b]) => {
      setDepMap(Object.fromEntries((d.data || []).map((x) => [x.id, x.name])));
      setBrMap(Object.fromEntries((b.data || []).map((x) => [x.id, x.name])));
    }).catch(() => {});
  }, []);

  const positionOptions = useMemo(() =>
    Array.from(new Set(items.map((i) => i.position_name).filter(Boolean))).sort(),
    [items]
  );
  const departmentOptions = useMemo(() =>
    Array.from(new Set(items.map((i) => depMap[i.department_id] || "").filter(Boolean))).sort(),
    [items, depMap]
  );
  const branchOptions = useMemo(() =>
    Array.from(new Set(items.map((i) => brMap[i.branch_id] || "").filter(Boolean))).sort(),
    [items, brMap]
  );

  const filtered = useMemo(() => {
    const s = q.toLowerCase().trim();
    return items.filter((c) => {
      if (s && !(c.name || "").toLowerCase().includes(s) && !(c.email || "").toLowerCase().includes(s)) return false;
      if (pos !== "all" && c.position_name !== pos) return false;
      if (dep !== "all" && (depMap[c.department_id] || "") !== dep) return false;
      if (br !== "all" && (brMap[c.branch_id] || "") !== br) return false;
      if (statusFilter === "recycling" && !c.needs_recycling) return false;
      if (statusFilter === "ok" && c.needs_recycling) return false;
      return true;
    });
  }, [items, q, pos, dep, br, statusFilter, depMap, brMap]);

  const scrollBy = (dx) => { scrollRef.current?.scrollBy({ left: dx, behavior: "smooth" }); };

  if (loading) return <div className="text-slate-500 text-sm">Carregando colaboradores…</div>;
  if (items.length === 0) return null;

  return (
    <section data-testid="collaborators-carousel">
      <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="eyebrow">Equipe</div>
          <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight mt-1">Colaboradores da empresa</h2>
          <p className="text-slate-600 text-sm mt-1">
            {filtered.length} de {items.length} · progresso individual e alertas de reciclagem
          </p>
        </div>
        <div className="hidden md:flex gap-2">
          <Button size="icon" variant="outline" onClick={() => scrollBy(-800)} data-testid="carousel-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <Button size="icon" variant="outline" onClick={() => scrollBy(800)} data-testid="carousel-next"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>

      {/* Filtros */}
      <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 mb-4">
        <div className="relative sm:col-span-2">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nome ou e-mail…" className="pl-9" data-testid="carousel-search" />
        </div>
        <select className="border border-input rounded-md px-3 py-2 text-sm bg-white" value={pos} onChange={(e) => setPos(e.target.value)} data-testid="carousel-filter-position">
          <option value="all">Todos os cargos</option>
          {positionOptions.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select className="border border-input rounded-md px-3 py-2 text-sm bg-white" value={dep} onChange={(e) => setDep(e.target.value)} data-testid="carousel-filter-department">
          <option value="all">Todos os departamentos</option>
          {departmentOptions.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select className="border border-input rounded-md px-3 py-2 text-sm bg-white" value={br} onChange={(e) => setBr(e.target.value)} data-testid="carousel-filter-branch">
          <option value="all">Todas as filiais</option>
          {branchOptions.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>
      <div className="flex gap-2 mb-3 text-xs">
        {[["all", "Todos"], ["recycling", "Precisam reciclar"], ["ok", "Em dia"]].map(([v, l]) => (
          <button key={v} onClick={() => setStatusFilter(v)} data-testid={`carousel-status-${v}`}
            className={"px-3 py-1 rounded-full border " + (statusFilter === v ? "bg-[var(--unigr-navy)] text-white border-[var(--unigr-navy)]" : "border-[var(--unigr-border)] text-slate-600 hover:bg-slate-50")}>
            {l}
          </button>
        ))}
        <span className="text-slate-400 flex items-center gap-1"><Filter className="w-3 h-3" />filtros ativos</span>
      </div>

      <div ref={scrollRef} className="flex gap-4 overflow-x-auto no-scrollbar pb-2 -mx-1 px-1 snap-x snap-mandatory" data-testid="carousel-track">
        {filtered.length === 0 && (
          <div className="min-w-full py-10 text-center text-slate-500 text-sm">Nenhum colaborador com esses filtros.</div>
        )}
        {filtered.map((c) => (
          <Link
            key={c.id}
            to={`/collaborators/${c.id}`}
            data-testid={`colab-card-${c.id}`}
            className="min-w-[320px] max-w-[320px] snap-start"
          >
            <Card className="border-[var(--unigr-border)] hover:shadow-md hover:border-[var(--unigr-navy)] transition-all cursor-pointer h-full">
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold shrink-0"
                       style={{ backgroundColor: avatarColor(c.name) }}>{initials(c.name)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-base truncate">{c.name}</div>
                    <div className="text-xs text-slate-500 truncate">{c.position_name || "—"}</div>
                  </div>
                  {c.needs_recycling && (
                    <div className="relative shrink-0" title="Possui certificado vencido ou vencendo em breve" data-testid={`colab-bell-${c.id}`}>
                      <Bell className="w-5 h-5 text-[var(--unigr-red)]" />
                      <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-[var(--unigr-red)] animate-pulse" />
                    </div>
                  )}
                </div>
                <div className="mt-4">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-slate-500">Progresso geral</span>
                    <span className="font-mono font-semibold">{c.overall_progress}%</span>
                  </div>
                  <Progress value={c.overall_progress} className="h-2" />
                  <div className="text-[11px] text-slate-500 mt-1">{c.completed_courses}/{c.total_courses} cursos concluídos</div>
                </div>
                <div className="mt-4 border-t border-[var(--unigr-border)] pt-3 space-y-1.5 max-h-[152px] overflow-y-auto no-scrollbar">
                  {c.courses.length === 0 && <div className="text-xs text-slate-400 italic">Sem trilha vinculada.</div>}
                  {c.courses.map((cc) => (
                    <div key={cc.id} className="flex items-center gap-2 text-xs">
                      {STATUS_ICON[cc.status] || STATUS_ICON.pending}
                      <span className="flex-1 truncate" title={cc.title}>{cc.title}</span>
                      {cc.cert_status === "expired" && <Badge className="bg-rose-100 text-rose-700 border-0 text-[9px] px-1.5 py-0">vencido</Badge>}
                      {cc.cert_status === "expiring_soon" && <Badge className="bg-amber-100 text-amber-700 border-0 text-[9px] px-1.5 py-0">vence</Badge>}
                      {cc.status === "in_progress" && cc.cert_status !== "expired" && <span className="font-mono text-slate-500">{cc.progress}%</span>}
                      {cc.status === "completed" && cc.cert_status === "valid" && <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[9px] px-1.5 py-0">em dia</Badge>}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    </section>
  );
}
